import {
    ConfirmMapRequestsInput,
    ImklDataValidationStatus,
    MapRequestDetailUnaStatus,
    MapRequestNotInvolvedInput,
    ValidationStep
} from '@/api/klip-api.proxy';
import {EAlertType} from '@/app/shared/state/AlertModule';
import {organisationSettings} from '@/app/settings/settings-routes';
import {useUnaStore} from '../../una-store';
import {useUserStore} from '@/app/shared/state/UserDataModule';
import {EUserRole} from '@/app/shared/state/user-role';
import KlSteps from '@/app/una/components/kl-steps/kl-steps.vue';
import {AdminMapRequestDetailAsUna} from '@/app/admin/admin-routes';
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from '@/router/router-vue3';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {useAlertHandler} from '@/plugins/alert';
import {usePermission} from '@/plugins/permission';
import {RouteRecordRaw} from 'vue-router';
import DateUtil from '@/app/shared/helpers/date-util';
import {isEmpty} from 'lodash-es';

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlUnaAnswer',
    components: { KlSteps },
    props: {
        status: {
            type: Number as unknown as () => MapRequestDetailUnaStatus,
            required: true
        },
        lastStatusChange: {
            type: String,
            required: false,
            default: undefined,
        },
        mapRequestId: {
            type: String,
            required: true
        },
        zoneId: {
            type: String,
            required: true
        },
        zoneName: {
            type: String,
            required: true
        },
        isArchived: {
            type: Boolean,
            required: true
        },
        isClosed: {
            type: Boolean,
            required: false,
            default: false
        },
        downloadArchiveUrl: {
            type: String,
            required: false,
            default: undefined,
        },
        downloadImklUrl: {
            type: String,
            required: false,
            default: undefined,
        },
        downloadKlipPackageUrl : {
            type: String,
            required: false,
            default: undefined,
        },
        mapAssetsReady: {
            type: Boolean,
            required: false,
            default: false
        },
    },
    setup(props) {

        const route = useRoute();
        const router = useRouter();

        const imklRequirementsLink = import.meta.env.VITE_LINKS_IMKLINFO;

        const confirmSending = ref<boolean>(false);
        //const verifyingNamespace = ref<boolean>(false);
        const notInvolvedSending = ref<boolean>(false);
        //const mapAssetsLoading = ref<boolean>(true);
        //const mapAssetsReady = ref<boolean>(false);
        const showUpload = ref<boolean>(false);
        const isProcessing = ref<boolean>(false);
        const isValidating = ref<boolean>(false);
        //const namespace = ref<string>('');
        const validationSteps = ref<Array<ValidationStep>>(null);
        //const hasReceivedValidateImklUploadTask = ref<boolean>(false);
        //const dateStatusChangedFromUi = ref<string>(null);

        const isUnaManager = computed((): boolean => {
            return usePermission().hasPermission([EUserRole.una_manager]);
        })

        const unaSettingsLocation = computed((): RouteRecordRaw => {
            // return { name: organisationSettings.name, hash: organisationSettings.hash };
            return organisationSettings;
        });

        const dateLastStatusChange = computed(() => {
            // if (!!dateStatusChangedFromUi.value) {
            //     return dateStatusChangedFromUi.value;
            // }
            return DateUtil.formatDateTime(props.lastStatusChange);
        });

        const hasDateLastStatusChange = computed(() => {
            return !!props.lastStatusChange;
        });

        const canAnswer = computed((): boolean => {
            return !showUpload.value && !isProcessing.value && !props.isClosed;
        });

        const canConfirm = computed((): boolean => {
            return isStateAssigned.value && !props.isArchived && isUnaWriter.value;
        });

        const isStateAssigned = computed((): boolean => {
            return props.status === MapRequestDetailUnaStatus.Assigned;
        });

        const isStateConfirmed = computed((): boolean => {
            return props.status === MapRequestDetailUnaStatus.Confirmed;
        });

        const isStateConfirmedInvalidImkl = computed((): boolean => {
            return props.status === MapRequestDetailUnaStatus.ConfirmedInvalidImkl;
        });

        const isStateConfirmedUploadedImkl = computed((): boolean => {
            return props.status === MapRequestDetailUnaStatus.ConfirmedUploadedImkl;
        });

        const isStateNotAnswered = computed((): boolean => {
            return props.status === MapRequestDetailUnaStatus.NotAnswered;
        });

        const isStateNotInvolved = computed((): boolean => {
            return props.status === MapRequestDetailUnaStatus.NotInvolved;
        });

        const isStateResponded = computed((): boolean => {
            //_isMapReady(); // ???? async backend status fetch in computed ????
            return props.status === MapRequestDetailUnaStatus.Responded;
        });

        const isStateShouldRespondToMri = computed((): boolean => {
            //_isMapReady(); // ???? async backend status fetch in computed ????
            return props.status === MapRequestDetailUnaStatus.ShouldRespondToMri;
        });

        const classes = computed(() => {
            if (isStateAssigned.value || (isStateConfirmed.value && !showUpload.value)) {
                return 'vl-u-highlight-content';
            }
            return '';
        });

        const isUnaWriter = computed((): boolean => {
            return useUserStore().hasRole([EUserRole.una_operator]) || viewingAsImpersonatedUser.value;
        });

        const imklUploadUrl = computed((): string => {
            // really??
            return `/bff/Una/maprequests/imkl/upload/${props.mapRequestId}/${props.zoneId}`;
        });

        const viewingAsImpersonatedUser = computed(() => {
            return route.name === AdminMapRequestDetailAsUna.name;
        });

        const prepareImklUpload = () => {
            showUpload.value = true;
        }

        const replaceIMKL = () => {
            prepareImklUpload();
        }

        const cancelUpload = () => {
            showUpload.value = false;
        }

        const confirm = () => {
            confirmSending.value = true;
            useAlertHandler().removeAlert('kl-una-answer_confirmed');
            _confirmMapRequests([new ConfirmMapRequestsInput({ zoneId: props.zoneId, mapRequestId: props.mapRequestId })])
                .then().catch((error) => {
                    useAlertHandler().addAlert({
                        id: 'kl-una-answer_confirmed',
                        title: 'Server fout',
                        message: `We krijgen volgende status terug: <strong>${error.message ? error.message : error.status}<strong>`,
                        closable: true,
                        type: EAlertType.Error,
                    });
                });
        }

        const notInvolved = () => {
            const input = new MapRequestNotInvolvedInput(
                { mapRequestId: props.mapRequestId, zoneId: props.zoneId },
            );

            notInvolvedSending.value = true;
            useAlertHandler().removeAlert('kl-una-answer_notInvolved');
            _replyNotInvolved(input)
                .then(() => {})
                .catch((error) => {
                    useAlertHandler().addAlert({
                        id: 'kl-una-answer_notInvolved',
                        title: 'Server fout',
                        message: `We krijgen volgende status terug: <strong>${error.message ? error.message : error.status}<strong>`,
                        closable: true,
                        type: EAlertType.Error,
                    });
            }).finally(() => {
                //notInvolvedSending.value = false; // moved to watch status
            });
        }

        const fetchDownloadKlipPackageUrl = () => {
            if (props.downloadKlipPackageUrl) {
                window.open(props.downloadKlipPackageUrl);
            }
        }

        const navigateToViewer = () => {
            const routeData = router.resolve({ name: 'unaviewer', params: { mapRequestId: props.mapRequestId, zoneId: props.zoneId } });
            window.open(routeData.href, '_blank');
        }

        const _fetchValidationErrors = async () => {
            if (props.isArchived) {
                return;
            }

            console.assert(props.status === MapRequestDetailUnaStatus.ConfirmedInvalidImkl, 'Invalid status for _fetchFileStatus', props.status);

            isProcessing.value = true;

            const response = await useKlipApiProxy().una_GetImklFileUploadStatus(props.mapRequestId, props.zoneId);
            if (!isEmpty(response.result.validationSteps)) {
                validationSteps.value = response.result.validationSteps.sort((a, b) => a.order - b.order);
            }

            isProcessing.value = false;
        }

        const _confirmMapRequests = (input: ConfirmMapRequestsInput[]) => useUnaStore().confirmMapRequests(input);
        const _replyNotInvolved = (input: MapRequestNotInvolvedInput) => useUnaStore().notInvolved(input);

        // TODO: review/refactor
        watch(
            () => props.status,
            (newStatus: MapRequestDetailUnaStatus) => {

                // TODO: review
                notInvolvedSending.value = false;
                isProcessing.value = false;
                confirmSending.value = false;

                isValidating.value = newStatus === MapRequestDetailUnaStatus.ConfirmedUploadedImkl;
                showUpload.value = isValidating.value; // validate message is shown in kl-upload-imkl

                if (newStatus === MapRequestDetailUnaStatus.ConfirmedInvalidImkl) {
                    _fetchValidationErrors();
                }
            },
            {immediate: true, deep: true}
        )


        return {
            confirmSending,
            notInvolvedSending,
            // mapAssetsLoading,
            // mapAssetsReady,
            showUpload,
            isProcessing,
            isValidating,
            // namespace,
            validationSteps,
            // hasReceivedValidateImklUploadTask,
            // dateStatusChangedFromUi,
            imklRequirementsLink,
            prepareImklUpload,
            replaceIMKL,
            cancelUpload,
            confirm,
            notInvolved,
            isUnaManager,
            unaSettingsLocation,
            // currentStatus,
            dateLastStatusChange,
            hasDateLastStatusChange,
            canAnswer,
            canConfirm,
            isStateAssigned,
            isStateConfirmed,
            isStateConfirmedInvalidImkl,
            isStateConfirmedUploadedImkl,
            isStateNotAnswered,
            isStateNotInvolved,
            isStateResponded,
            isStateShouldRespondToMri,
            classes,
            //hasNamespace,
            isUnaWriter,
            imklUploadUrl,
            fetchDownloadKlipPackageUrl,
            viewingAsImpersonatedUser,
            navigateToViewer,
        };
    }
})
